/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isnil from 'lodash.isnil';
import RouteManager from '@ion/applications/src/routes/RouteManager';
import {info} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GenericObject} from '@ion/core/src/types/entities';
import changePage, {
  getRoute,
  getRouteByFeature,
  isRoute,
  Route,
  setPersistedRoute
} from '@/routes/RouteDefinitions';
import {getDefaultPageId} from '@/routes/RouteSecurity';
import Features, {TRACKING_PAGE2} from '@/security/Features';
import {hasPage} from '@/security/helpers';
import {NavState} from '@/store/reducers/nav';
import {Page} from '@/types/pages';
import {toggleAccessError} from './helpers';
import {preinitialization} from './initialization';

export const SET_PAGE                  = 'SET_PAGE';
export const SET_SIDE_SHEET_STATUS     = 'SET_SIDE_SHEET_STATUS';
export const SET_ASSET_DETAIL_VIEW     = 'SET_ASSET_DETAIL_VIEW';
export const TOGGLE_SECONDARY_NAV      = 'TOGGLE_SECONDARY_NAV';
export const TOGGLE_USER_PROFILE_VIEW  = 'TOGGLE_USER_PROFILE_VIEW';
export const PAGE_SECURITY_INITIALIZED = 'PAGE_SECURITY_INITIALIZED';
export const SET_PREVIOUS_PAGE         = 'SET_PREVIOUS_PAGE';
export const SET_IGNORE_QUERY          = 'SET_IGNORE_QUERY';
export const TOGGLE_SIDESHEET_ARROW    = 'TOGGLE_SIDESHEET_ARROW';
export const SET_MOBILE_LIST_VISIBLE   = 'SET_MOBILE_LIST_VISIBLE';
export const SET_ACTIVE_INDEX          = 'SET_ACTIVE_INDEX';
export const SET_GRID_OPTIONS          = 'SET_GRID_OPTIONS';
export const SET_DEEP_LINK_RESTRICTION = 'SET_DEEP_LINK_RESTRICTION';
export const SET_LAYOUT_OFFSET_HEIGHT  = 'SET_LAYOUT_OFFSET_HEIGHT';
export const SET_NAV_COLLAPSED_STATUS  = 'SET_NAV_COLLAPSED_STATUS';

export const setLayoutOffsetHeight = (height: number) => ({
  type : SET_LAYOUT_OFFSET_HEIGHT, height
});

export const setNavCollapsedStatus = collapsed => (
  {type: SET_NAV_COLLAPSED_STATUS, collapsed}
);

/**
 * @setPage
 *
 * The setPage action is the initial action that is invoked when an iOn page
 * is loaded. The first call will determine if the pre-initialization phase
 * has been completed; if not, the application pre-initialization process
 * is invoked.
 */
export const setPage = page => {
  return (dispatch, getState) => {
    const modified = page === getRoute(Route.DASHBOARD) ? getRoute(Route.FLEET_DASHBOARD) : page;
    if (!getState().initialization.preInitializationComplete) {
      dispatch(preinitialization());
    }
    const {pages}: NavState = getState().nav;
    if (isPermittedPage(pages, modified)) {
      if (!isRoute(Route.LOGIN)) {
        setPersistedRoute(modified);
      }
      dispatch({type: SET_PAGE, page: modified});
    }
  };
};

/**
 * @initializePageSecurity
 *
 * Invoked exclusively via the initializationComplete action creator upon completion
 * of the application initialization phase, at which point a route change listener is
 * added to the Next.js Router which, upon a route change event, verifies that the
 * User has permission to view the given route / page.
 */
export const initializePageSecurity = () => {
  return (dispatch, getState) => {
    if (!getState().nav.pageSecurityInitialized) {
      RouteManager.addEventListener('routeChangeStart', function(url) {
        verifyRouteChange(url, dispatch, getState);
      });
      info('iOn Page Security initialized');
      dispatch({type: PAGE_SECURITY_INITIALIZED});
    }
  };
};

/**
 * @function toggleSecondaryNav
 */
export const toggleSecondaryNav = (show: boolean) => (
  {type: TOGGLE_SECONDARY_NAV, show}
);

// dispatch when the error occured in deep link mode...
export const setDeepLinkRestriction = (restriction: boolean) => (
  {type: SET_DEEP_LINK_RESTRICTION, restriction}
);

// dispatch to set the nav active index...
export const setActiveIndex = (index: number) => ({type: SET_ACTIVE_INDEX, index});

/**
 * @function toggleUserProfileView
 */
export const toggleUserProfileView = (view: boolean) => (
  {type: TOGGLE_USER_PROFILE_VIEW, view}
);

/**
 * @setSideSheetStatus
 */
export const setSideSheetStatus = data => (
  {type: SET_SIDE_SHEET_STATUS, data}
);

/**
 * Retrieves the fallback page ID from the given array of pages.
 * The fallback page is determined as the first active, preview page with a valid route by feature.
 *
 * @param pages An array of pages to search through.
 * @returns The ID of the fallback page, or undefined if no fallback page is found.
 */
export const getFallbackPageId = (pages: Page[]): string => {
  const findPage = (pages: Page[]): Page => {
    for (const page of pages) {
      const {isActive, isPreview, id, pages: subPages} = page;
      if (isActive && isPreview && !isnil(getRouteByFeature(id, false))) {
        return page;
      }
      if (subPages) {
        const foundPage = findPage(subPages as Page[]);
        if (foundPage) {
          return foundPage;
        }
      }
    }
  };
  return findPage(pages)?.id;
};

/**
 * Redirects the user to the default page based on user preferences.
 * If the default page is not accessible, it falls back to a suitable alternative.
 *
 * @param dispatch - The dispatch function from the Redux store.
 * @param pages - An array of available pages.
 */
export const redirectToDefaultPage = (dispatch: Function, pages: Page[]) => {
  dispatch(toggleAccessError(true, 'nav:pageAccessError'));
  const defaultPageId = getDefaultPageId(UserPreferences.get('defaultPageId') || TRACKING_PAGE2);
  const redirectPage  = getRouteByFeature(
    Features.hasFeature(defaultPageId) ? defaultPageId : getFallbackPageId(pages)
  );
  // determine if query parameters should be included in the redirect URL
  const includeQueryParams = (
    redirectPage === getRoute(Route.TRACKING2) || redirectPage === getRoute(Route.REPORTS)
  );
  changePage(redirectPage as Route, undefined, includeQueryParams);
};

/**
 * @mapTrackingToBetaRoute
 *
 * Redirect tracking page requests to the beta version.
 * This function checks if the provided URL contains the old tracking route and
 * replaces it with the new tracking2 route.
 *
 * @param decodedURL The URL to be checked and potentially modified.
 * @returns The modified URL if the replacement was made, otherwise the original URL.
 */
const mapTrackingToBetaRoute = (decodedURL: string): string => {
  if (decodedURL) {
    if (decodedURL?.includes(Route.TRACKING) && !decodedURL?.includes(Route.TRACKING2)) {
      return decodedURL?.replace(Route.TRACKING, Route.TRACKING2);
    }
  }
  return decodedURL;
};

/**
 * @verifyRouteChange
 */
export const verifyRouteChange = (url: string, dispatch: Function, getState: Function) => {
  const {pages, currentPage}: NavState = getState().nav;
  const decodedURL = mapTrackingToBetaRoute(decodeURIComponent(url).split('#')[0]);
  if (decodedURL !== currentPage) {
    if (isPermittedPage(pages, decodedURL)) {
      dispatch(setPage(decodedURL));
    } else {
      if (currentPage) {
        info('page changing to:', currentPage);
        changePage(currentPage as Route);
      } else if (RouteManager.getAsPath() === '/') {
        RouteManager.back();
      } else {
        redirectToDefaultPage(dispatch, pages);
      }
      throw `[iOn Suite Security Violation]: Route ${decodedURL} is not permitted. This error can safely be ignored`;
    }
  }
};

/**
 * @isPermittedPage
 */
const isPermittedPage = (pages: Page[], uri: string): boolean => {
  return hasPage({pages, uri: uri.split('?')[0]});
};

/**
 * @setPreviousPage
 */
export const setPreviousPage = route => (
  {type: SET_PREVIOUS_PAGE, route}
);

/**
 * @setIgnoreQuery
 */
export const setIgnoreQuery = value => (
  {type: SET_IGNORE_QUERY, value}
);

/**
 * @toggleSideSheetArrow
 */
export const toggleSideSheetArrow = (isVisible: boolean) => (
  {type: TOGGLE_SIDESHEET_ARROW, isVisible}
);

/**
 * @setMobileListVisible
 *
 * Controls whether the tracking asset list's mobile bottom sheet is shown -
 * defaults to visible; the list's Close button hides it (revealing the full
 * map), and a reopen tab brings it back.
 */
export const setMobileListVisible = (isVisible: boolean) => (
  {type: SET_MOBILE_LIST_VISIBLE, isVisible}
);

export const setGridOptions = (options: GenericObject) => (
  {type: SET_GRID_OPTIONS, options}
);
